import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import { UserBadge } from '../data/badge';
import { Step } from '../data/step';
import { clientState } from '../states/auth';

interface AcademyData {
  levels: {
    [levelId: string]: Array<Step>;
  };
  badges: Array<UserBadge>;
}

export function useAcademy() {
  const client = useRecoilValue(
    clientState({
      contentType: 'application/json',
      apiVersion: 1,
    })
  );

  const { data, isLoading, refetch } = useQuery<{
    data: { success: boolean; data: AcademyData };
  }>(['academyLevels'], () => client.get('/academy'), {
    refetchOnWindowFocus: false,
  });

  const levels = data?.data.data.levels ?? {};
  const badges = data?.data.data.badges ?? [];

  // Helper function to find the first level with at least one incomplete step
  const getCurrentLevel = (): number => {
    const levelIds = Object.keys(levels)
      .map(id => parseInt(id, 10))
      .sort((a, b) => a - b);

    for (const levelId of levelIds) {
      const steps = levels[levelId.toString()] || [];
      // Check if any step is incomplete
      if (steps.some(step => !step.userStep?.completedAt)) {
        return levelId;
      }
    }

    // If all levels are complete, return the last level or default to 1
    return levelIds.length > 0 ? levelIds[levelIds.length - 1] : 1;
  };

  // Helper function to get steps for a specific level
  const getLevelSteps = (levelId?: number): Array<Step> => {
    const effectiveLevelId =
      levelId !== undefined ? levelId : getCurrentLevel();
    return levels[effectiveLevelId.toString()] || [];
  };

  // Helper function to count remaining lessons in current level
  const lessonsRemainingInCurrentLevel = (): number => {
    const currentLevelSteps = getLevelSteps();
    return currentLevelSteps.filter(step => !step.userStep?.completedAt).length;
  };

  return {
    levels,
    badges,
    getLevelSteps,
    getCurrentLevel,
    lessonsRemainingInCurrentLevel,
    refetch,
    isLoading,
  };
}
